import { useFlags } from 'flagsmith/react';

import { Platform, SubscriptionTier } from '@invoice-simple/common';
import { FeatureName } from '@invoice-simple/feature-gate';
import { getCouponDiscount } from '@invoice-simple/is-coupon';

import { useISIntl } from 'src/i18n';
import UserModel, { Cadence } from 'src/models/UserModel';
import { BookkeepingTier, getBookkeepingBundlePrice } from 'src/util/subscriptions';
import titleize from 'src/util/Titleize';
import { hasValidDiscount } from './discount.utils';
import { messages } from './messages';
import { getFormattedCurrency } from './paywall.utils';

export const orderedTiers = [
  SubscriptionTier.ESSENTIALS,
  SubscriptionTier.PLUS,
  SubscriptionTier.PREMIUM
];

/**
 * Get new tier equivalent from old tier.
 */
export function getNewTierFromOldTier(tier: SubscriptionTier): SubscriptionTier {
  switch (tier) {
    case SubscriptionTier.STARTER:
      return SubscriptionTier.ESSENTIALS;
    case SubscriptionTier.PRO:
      return SubscriptionTier.PLUS;
  }
  return tier;
}

/**
 * Get old tier equivalent from new tier.
 */
export function maybeGetOldTierFromNewTier(tier: SubscriptionTier): SubscriptionTier {
  // No need to map to old tier name if we're offering new tiered pricing
  const user = UserModel.getInstance();
  if (user.shouldOfferNewTieredPricing) {
    return tier;
  }

  switch (tier) {
    case SubscriptionTier.PREMIUM_LEGACY:
      return SubscriptionTier.PREMIUM;
    case SubscriptionTier.ESSENTIALS:
      return SubscriptionTier.STARTER;
    case SubscriptionTier.PLUS:
      return user.legacyUsageGroup ? tier : SubscriptionTier.PRO;
  }
  return tier;
}

export function getTierRequired(
  reasonForUpgrade:
    | FeatureName
    | '3-invoice-limit'
    | '10-invoice-limit'
    | 'plus-direct-link'
    | 'premium-direct-link'
    | null
): SubscriptionTier | null {
  if (!reasonForUpgrade) return null;

  const featureToTierMap = {
    [FeatureName.INVOICE_PHOTOS]: SubscriptionTier.PLUS,
    [FeatureName.SIGNATURE]: SubscriptionTier.PLUS,
    [FeatureName.REQUEST_REVIEW]: SubscriptionTier.PLUS,
    [FeatureName.DUE_DATE_REMINDERS]: SubscriptionTier.PLUS,
    [FeatureName.REPORTS]: SubscriptionTier.PLUS,
    [FeatureName.EXPENSES]: SubscriptionTier.PLUS,
    [FeatureName.CLIENT_SIGNATURE]: SubscriptionTier.PREMIUM,
    [FeatureName.DEPOSITS]: SubscriptionTier.PREMIUM,
    [FeatureName.PREMIUM_TEMPLATES]: SubscriptionTier.PREMIUM,
    [FeatureName.VERSION_HISTORY]: SubscriptionTier.PREMIUM,
    '3-invoice-limit': SubscriptionTier.PLUS,
    '10-invoice-limit': SubscriptionTier.PREMIUM,
    'plus-direct-link': SubscriptionTier.PLUS,
    'premium-direct-link': SubscriptionTier.PREMIUM
  };

  return featureToTierMap[reasonForUpgrade];
}

export enum SubscriptionSwitchType {
  UPGRADE = 'upgrade',
  CROSSGRADE = 'crossgrade',
  DOWNGRADE = 'downgrade',
  LEGACY_UPGRADE = 'legacy-upgrade'
}

export function getSubscriptionSwitchType(
  oldTier: SubscriptionTier | null,
  newTier: SubscriptionTier,
  addingBookkeeping?: boolean,
  removingBookkeeping?: boolean
): SubscriptionSwitchType {
  // Adding bookkeeping is always an immediate upgrade, regardless of tier movement.
  // Handles same-tier upsell (PLUS→PLUS+BK) and apparent downgrades (PREMIUM→ESSENTIALS+BK).
  if (addingBookkeeping) {
    return SubscriptionSwitchType.UPGRADE;
  }
  // Removing bookkeeping is always a downgrade, regardless of tier movement.
  // Handles apparent tier upgrades (PLUS+BK→PREMIUM) that lose the bookkeeping add-on.
  if (removingBookkeeping) {
    return SubscriptionSwitchType.DOWNGRADE;
  }
  // order matters
  if (oldTier === newTier) {
    return SubscriptionSwitchType.CROSSGRADE;
  }
  if (isLegacyUpgrade(oldTier, newTier)) {
    return SubscriptionSwitchType.LEGACY_UPGRADE;
  }
  if (isDowngradingTier(oldTier, newTier)) {
    return SubscriptionSwitchType.DOWNGRADE;
  }
  return SubscriptionSwitchType.UPGRADE;
}

function isDowngradingTier(oldTier: SubscriptionTier | null, newTier: SubscriptionTier): boolean {
  if (oldTier === null) {
    return false;
  }
  return subscriptionTierPriority[oldTier] < subscriptionTierPriority[newTier];
}

export function isEndOfTermSwitch(switchType: SubscriptionSwitchType): boolean {
  return (
    switchType === SubscriptionSwitchType.CROSSGRADE ||
    switchType === SubscriptionSwitchType.DOWNGRADE
  );
}

/* Descending priority order */
const subscriptionTierPriority: Record<SubscriptionTier, number> = {
  [SubscriptionTier.PREMIUM]: 0,
  [SubscriptionTier.PREMIUM_LEGACY]: 1,
  [SubscriptionTier.PLUS_LEGACY]: 2,
  [SubscriptionTier.ESSENTIALS_LEGACY]: 3,
  [SubscriptionTier.PAYMENTS_TIER]: 4,
  [SubscriptionTier.PRO]: 5,
  [SubscriptionTier.PLUS]: 6,
  [SubscriptionTier.STARTER]: 7,
  [SubscriptionTier.ESSENTIALS]: 8
};

export type TierCardDataType = {
  name: SubscriptionTier;
  description: string;
  featureTitle?: string;
  features: string[];
};

type TierCardDataArgs = {
  isMobile: boolean;
};

export const tierCardData = (args: TierCardDataArgs): TierCardDataType[] => {
  const { ft } = useISIntl();
  const { pulsem_reviews } = useFlags(['pulsem_reviews']);

  return [
    {
      name: SubscriptionTier.ESSENTIALS,
      description: ft(messages.essentialsPlanDesc),
      features: [
        ft(messages.invoicesPerMonthAmount, { invoices: 3 }),
        ft(messages.onlineCollaboration),
        ft(messages.creditCardProcessing),
        ft(messages.featureQR),
        ft(messages.acceptDeposits)
      ]
    },
    {
      name: SubscriptionTier.PLUS,
      description: ft(messages.plusPlanDesc),
      featureTitle: ft(messages.everythingInTier, { tier: titleize(SubscriptionTier.ESSENTIALS) }),
      features: [
        ft(messages.invoicesPerMonthAmount, { invoices: 10 }),
        ft(messages.businessOwnerSignature),
        ft(messages.businessSummaryReports),
        ft(messages.featureDueDateReminders)
      ]
    },
    {
      name: SubscriptionTier.PREMIUM,
      description: ft(messages.premiumPlanDesc),
      featureTitle: ft(messages.everythingInTier, { tier: titleize(SubscriptionTier.PLUS) }),
      features: [
        ft(messages.unlimitedInvoices),
        ...(pulsem_reviews.enabled ? [ft(messages.automatedReviews)] : []),
        ft(messages.clientSignatures),
        ft(messages.versionHistory)
      ]
    }
  ];
};

export const getTierPrice = (tier: SubscriptionTier, cadence: Cadence) => {
  const user = UserModel.getInstance();
  const couponInfo = user.paywallCouponInfo;

  const regularPrice = getFormattedCurrency(user, user.getGeoSubAmount({ tier, cadence }) / 100);

  const hasDiscount = couponInfo && hasValidDiscount(couponInfo, cadence);

  if (hasDiscount) {
    const displayPrice = getFormattedCurrency(
      user,
      user.getGeoSubAmount({
        tier,
        cadence,
        coupon: couponInfo
      }) / 100
    );

    return { comparePrice: regularPrice, displayPrice };
  }

  const comparePrice =
    cadence === Cadence.ANNUAL
      ? getFormattedCurrency(
          user,
          (user.getGeoSubAmount({ tier, cadence: Cadence.MONTHLY }) / 100) * 12
        )
      : undefined;

  return { comparePrice, displayPrice: regularPrice };
};

export const getBookkeepingTierPrice = (
  tier: BookkeepingTier,
  cadence: Cadence,
  currency: string,
  basePlanPrice: number
): { regularPrice: number; discountedPrice?: number } => {
  const user = UserModel.getInstance();
  const couponInfo = user.paywallCouponInfo;
  const countryCode = user.activeCountryCode;
  const hasDiscount = couponInfo && hasValidDiscount(couponInfo, cadence);

  const regularPrice = getBookkeepingBundlePrice(tier, cadence, currency, basePlanPrice);

  if (hasDiscount) {
    const discountAmount = getCouponDiscount({
      paywallCouponConfig: couponInfo,
      tier,
      cadence,
      platform: Platform.WEB,
      countryCode
    });

    if (!discountAmount) return { regularPrice, discountedPrice: regularPrice };

    let discountedPrice = regularPrice;

    switch (couponInfo?.discountType) {
      case 'percentage':
        discountedPrice = Math.floor(regularPrice - (regularPrice * discountAmount) / 100);
        break;
      case 'amount':
        discountedPrice = regularPrice - discountAmount;
        break;
      case 'fixed-price':
        discountedPrice = discountAmount;
        break;
    }

    return { regularPrice, discountedPrice };
  }

  return { regularPrice };
};

export const isModernTier = (tier: SubscriptionTier | null) => {
  return (
    tier === SubscriptionTier.ESSENTIALS ||
    tier === SubscriptionTier.PLUS ||
    tier === SubscriptionTier.PREMIUM
  );
};

export const isLegacyUpgrade = (oldTier: SubscriptionTier | null, newTier: SubscriptionTier) => {
  const isNewSubModern = isModernTier(newTier) || newTier === SubscriptionTier.PREMIUM_LEGACY;
  return !isModernTier(oldTier) && isNewSubModern;
};

export const getTierTitle = (tier: SubscriptionTier) => {
  switch (tier) {
    case SubscriptionTier.PLUS_LEGACY:
      return SubscriptionTier.PLUS;
    case SubscriptionTier.PREMIUM_LEGACY:
      return SubscriptionTier.PREMIUM;
    default:
      return tier;
  }
};
